import React, { useRef } from 'react';
import { createPortal } from 'react-dom';

export type BulkIsbnProgress = { sent: number; received: number; total: number };

export interface BulkIsbnPasteModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  description: string;
  value: string;
  onChange: (value: string) => void;
  parsedCount: number;
  onSubmit: () => void;
  submitButtonLabel: string;
  submitting?: boolean;
  progress?: BulkIsbnProgress | null;
  canClose?: boolean;
  /** When not showing progress, override the status line (e.g. "N valid ISBNs will be synced to DB") */
  statusLine?: React.ReactNode;
}

const overlayStyle: React.CSSProperties = {
  alignItems: 'flex-start',
  justifyContent: 'center',
  paddingTop: 'min(2rem, 10vh)',
  paddingBottom: '2rem',
  overflowY: 'auto',
};

const contentStyle: React.CSSProperties = {
  maxWidth: '560px',
  width: '90%',
  maxHeight: '85vh',
  display: 'flex',
  flexDirection: 'column',
  overflowY: 'auto',
  flexShrink: 0,
};

const textareaStyle: React.CSSProperties = {
  width: '100%',
  minHeight: '120px',
  flex: '1 1 0',
  padding: '0.75rem',
  fontSize: '0.875rem',
  fontFamily: 'monospace',
  border: '1px solid var(--border-primary)',
  borderRadius: '0.25rem',
  resize: 'none',
  marginBottom: '0.75rem',
  overflowY: 'auto',
};

/**
 * Shared modal for pasting ISBNs in bulk (upload or DB sync).
 * Parent controls value/onChange, parsedCount, and submit/progress state.
 */
const BulkIsbnPasteModal: React.FC<BulkIsbnPasteModalProps> = ({
  open,
  onClose,
  title,
  description,
  value,
  onChange,
  parsedCount,
  onSubmit,
  submitButtonLabel,
  submitting = false,
  progress = null,
  canClose = true,
  statusLine: statusLineOverride,
}) => {
  const mouseDownInModalRef = useRef(false);

  if (!open) return null;

  const showProgress = submitting || (progress && progress.total > 0);
  const defaultStatusLine =
    parsedCount > 0
      ? `${parsedCount} valid ISBN${parsedCount !== 1 ? 's' : ''} will be queued`
      : 'No valid ISBNs yet';
  const statusLine = showProgress
    ? (
        <>
          Sent: {progress?.sent ?? 0} / {progress?.total ?? 0}
          {progress && progress.total > 0 && (
            <> · Received (accepted): {progress.received} / {progress.total}</>
          )}
        </>
      )
    : (statusLineOverride !== undefined ? statusLineOverride : defaultStatusLine);

  return createPortal(
    <div
      className="modal-overlay"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) mouseDownInModalRef.current = false;
      }}
      onClick={(e) => {
        if (e.target !== e.currentTarget) return;
        if (mouseDownInModalRef.current) return;
        if (!canClose) return;
        onClose();
      }}
      style={overlayStyle}
    >
      <div
        className="modal-content"
        onMouseDown={() => { mouseDownInModalRef.current = true; }}
        onClick={(e) => e.stopPropagation()}
        style={contentStyle}
      >
        <h3 style={{ margin: '0 0 1rem 0', fontSize: '1.125rem', flexShrink: 0 }}>{title}</h3>
        <p style={{ margin: '0 0 0.75rem 0', fontSize: '0.875rem', color: 'var(--text-secondary)', flexShrink: 0 }}>
          {description}
        </p>
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="e.g. 9780123456789&#10;9780123456790&#10;..."
          style={textareaStyle}
          spellCheck={false}
        />
        <div style={{ marginBottom: '1rem', fontSize: '0.875rem', color: 'var(--text-secondary)', flexShrink: 0 }}>
          {statusLine}
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', flexShrink: 0 }}>
          {submitting ? (
            /* Sending: no buttons, modal cannot be closed */
            null
          ) : progress && progress.total > 0 ? (
            /* After sent: only Close */
            <button
              type="button"
              className="header-nav-button"
              onClick={onClose}
            >
              Close
            </button>
          ) : (
            /* Before send: Cancel + Send */
            <>
              <button
                type="button"
                className="header-nav-button"
                onClick={onClose}
              >
                Cancel
              </button>
              <button
                type="button"
                className="header-nav-button"
                onClick={onSubmit}
                disabled={parsedCount === 0}
                title={parsedCount === 0 ? 'Paste at least one valid ISBN' : undefined}
              >
                {`${submitButtonLabel} (${parsedCount})`}
              </button>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default BulkIsbnPasteModal;
